import React, { Component } from "react";
import type { ApolloClient } from "@apollo/client";
import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { get, isNil } from "lodash";
import { bankConstants } from "actions/types";
import { createLoadingSelector } from "store/selectors";
import { getPlaidLinkToken } from "actions/bankActions";
import type { RootState } from "types/state";

import Button from "./Button";
import Alert from "components/Alert";
import PlaidLinkTokenStorage from "../utils/PlaidLinkTokenStorage";

// Plaid Link is loaded from a global script tag (window.Plaid); it is not
// declared in types/globals.d.ts, so type the slice this component touches
// locally and cast window at the single call site below.
interface PlaidExitError {
  error_code?: string;
}
interface PlaidHandler {
  open: () => void;
  destroy: () => void;
}
interface PlaidLinkConfig {
  token: string;
  // onExit forwards to the async this.onExit handler; allow a returned promise.
  onExit: (
    err: PlaidExitError | null,
    metadata: unknown
  ) => void | Promise<void>;
  onSuccess: (token: string, metadata: unknown) => void;
}

const loadingSelector = createLoadingSelector([
  bankConstants.LINK_BANK_WITH_ICON,
  bankConstants.GET_PLAID_LINK_TOKEN,
]);

const mapStateToProps = (state: RootState) => {
  // profile carries arbitrary backend keys via an index signature, so
  // firstName/lastName are `unknown`; cast at the boundary to interpolate.
  const userLegalName = `${state.user.profile.firstName as string} ${
    state.user.profile.lastName as string
  }`;
  return {
    userId: state.user.id,
    isFetching: loadingSelector(state),
    userLegalName,
  };
};

const mapDispatchToProps = {
  getPlaidLinkToken,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

interface PlaidLinkOwnProps {
  label?: string;
  // render receives the component instance (the parent forwards it on and
  // calls its handlers). Typed structurally to avoid forward-referencing the
  // class below; propTypes only declared `func`.
  render?: (instance: React.Component) => React.ReactNode;
  onSuccess?: (token: string, metadata: unknown, userLegalName: string) => void;
  onExit?: (metadata: unknown) => void;
  bank?: object;
}

type PlaidLinkProps = PropsFromRedux & PlaidLinkOwnProps;

interface PlaidLinkState {
  linkLoaded: boolean;
  error: string | null;
  isOpen: boolean;
}

class PlaidLink extends Component<
  WithApolloClient<PlaidLinkProps>,
  PlaidLinkState
> {
  static defaultProps = {
    label: "Link bank account",
    style: {
      padding: "6px 4px",
      outline: "none",
      background: "#FFFFFF",
      border: "2px solid #F1F1F1",
      borderRadius: "4px",
    },
  };

  _isMounted = false;
  plaidHandler: PlaidHandler | null = null;

  constructor(props: WithApolloClient<PlaidLinkProps>) {
    super(props);
    this.state = {
      linkLoaded: false,
      error: null,
      isOpen: false,
    };
  }

  componentDidMount() {
    this._isMounted = true;
  }

  componentWillUnmount() {
    this._isMounted = false;
    if (!this.state.isOpen) {
      // userId is `string | null` in UserState; storage methods take `string`.
      // The runtime tolerates null here — cast at the boundary.
      PlaidLinkTokenStorage.clearTokenContext(this.props.userId as string);
    }
  }

  onExit = async (err: PlaidExitError | null, metadata: unknown) => {
    // handle the invalid link token error
    if (err != null && err.error_code === "INVALID_LINK_TOKEN") {
      this.plaidHandler!.destroy();
      await this.createPlaidLink();
    }
    // handle general error
    if (err != null) {
      this.setState({ error: "Failed to initialize plaid" });
      console.error({ message: "Failed to initialize plaid", err });
    }
    PlaidLinkTokenStorage.clearTokenContext(this.props.userId as string);
    // preserve the original short-circuit invocation (onExit is optional)
    void (this.props.onExit && this.props.onExit(metadata));
  };

  createPlaidLink = async () => {
    try {
      const currentPath = window.location.pathname;
      const bankId = get(this.props, "bank.id");
      const plaidLinkToken = await this.props.getPlaidLinkToken(
        this.props.client as ApolloClient<object>,
        bankId
      );
      const token = get(plaidLinkToken, "data.linkToken");
      if (isNil(bankId)) {
        PlaidLinkTokenStorage.setTokenContext(
          this.props.userId as string,
          currentPath,
          token as string
        );
      }
      const plaidConfig: PlaidLinkConfig = {
        token: token as string,
        onExit: (err, metadata) => this.onExit(err, metadata),
        onSuccess: (token, metadata) => {
          PlaidLinkTokenStorage.clearTokenContext(this.props.userId as string);
          // onSuccess is invoked unconditionally here even though it is an
          // optional prop — preserve the original non-guarded call.
          this.props.onSuccess!(token, metadata, this.props.userLegalName);
        },
      };

      this.plaidHandler = (
        window as unknown as {
          Plaid: { create: (config: PlaidLinkConfig) => PlaidHandler };
        }
      ).Plaid.create(plaidConfig);
      if (this._isMounted) {
        this.setState({ linkLoaded: true });
      }
    } catch (error) {
      this.setState({
        error: "Failed to load bank information.",
      });
      throw error;
    }
  };

  handleOnClick = async () => {
    await this.createPlaidLink();
    if (this.plaidHandler) {
      this.plaidHandler.open();
      this.setState({ isOpen: true });
    }
  };

  render() {
    return (
      <>
        {this.state.error && <Alert msg={this.state.error} type="error" />}
        {(this.props.render && this.props.render(this)) || (
          <Button
            name="submit"
            disabled={this.state.linkLoaded}
            onClick={() => void this.handleOnClick()}
            btnLabel={this.props.label}
            loading={this.props.isFetching}
            withArrow
          />
        )}
      </>
    );
  }
}

export default connector(withApollo<PlaidLinkProps>(PlaidLink));
